import type { PublishedUnitSummary } from '@/lib/types/publicProperty';
import type { RentFrequencyType } from '@/lib/types/property';
import { formatPublicCurrency } from '@/lib/utils/publicCurrency';
import { calculatePublicMonthlyRent, resolvePublicRentFrequency } from './publicRentCalculations';

type FormatTotalRentOptions = {
  includeFrequency?: boolean;
};

type RentDisplayParts = {
  monthlyAmount: string;
  rateDetails: string;
  netAnnualPsf: number;
  additionalAnnualPsf: number | null;
};

export function formatEstimatedTotalLabel(
  _frequency: PublishedUnitSummary['rentFrequency'],
): string {
  return 'Estimated monthly total';
}

export function formatTotalRent(
  unit: PublishedUnitSummary,
  options: FormatTotalRentOptions = {},
): string {
  const includeFrequency = options.includeFrequency !== false;
  const frequencyLabel = includeFrequency ? ' Monthly' : '';
  const { monthlyAmount, rateDetails } = getPublicRentDisplayParts(unit);

  return `${monthlyAmount}${frequencyLabel} (${rateDetails})`;
}

export function formatMonthlyLeaseAmount(unit: PublishedUnitSummary): string {
  return getPublicRentDisplayParts(unit).monthlyAmount;
}

export function formatAnnualPsfRentBreakdown(unit: PublishedUnitSummary): string {
  return getPublicRentDisplayParts(unit).rateDetails;
}

export function formatAnnualNetPsfRentRange(
  units: PublishedUnitSummary[],
  fallback: string,
): string {
  const values = units
    .map((unit) => getPublicRentDisplayParts(unit).netAnnualPsf)
    .filter((value) => Number.isFinite(value) && value > 0);

  if (values.length === 0) {
    return fallback;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min !== max) {
    return `${formatPublicCurrency(min)} – ${formatPublicCurrency(max)} /SF Net`;
  }

  return `${formatPublicCurrency(min)} /SF Net`;
}

export function getPublicRentDisplayParts(unit: PublishedUnitSummary): RentDisplayParts {
  const monthlyTotal = calculatePublicMonthlyRent(unit);
  const monthlyAmount = formatPublicCurrency(monthlyTotal ?? 0);
  const effectiveFrequency = resolvePublicRentFrequency(unit);
  const netAnnualPsf = annualizePsf(unit.netRentPerSF, effectiveFrequency);
  const additionalAnnualPsf =
    unit.additionalRentPerSF !== null
      ? annualizePsf(unit.additionalRentPerSF, effectiveFrequency)
      : null;
  const basisLabel = unit.rentBasis === 'gross' ? 'Gross' : 'Net';
  const details = `${formatPublicCurrency(netAnnualPsf)}/SF ${basisLabel}`;
  const additionalLabel =
    additionalAnnualPsf !== null && unit.rentBasis !== 'gross'
      ? ` + ${formatPublicCurrency(additionalAnnualPsf)}/SF Additional`
      : '';

  return {
    monthlyAmount,
    rateDetails: `${details}${additionalLabel}`,
    netAnnualPsf,
    additionalAnnualPsf,
  };
}

function annualizePsf(value: number, frequency: RentFrequencyType): number {
  return value * getAnnualFrequencyFactor(frequency);
}

function getAnnualFrequencyFactor(frequency: RentFrequencyType): number {
  switch (frequency) {
    case 'annually':
      return 1;
    case 'quarterly':
      return 4;
    case 'weekly':
      return 52;
    case 'daily':
      return 365;
    case 'monthly':
    default:
      return 12;
  }
}
